// Admin domain types — mirror apps/backend/src/types/admin.ts.
import { AdminRole, normalizeAdminRole } from "@safiri/shared";

// Roles live in @safiri/shared (shared with the backend's enforcement).
export { AdminRole };

export const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  [AdminRole.CEO]: "CEO",
  [AdminRole.BRANCH_MANAGER]: "Branch Manager (COO)",
  [AdminRole.REGISTRATION_TEAM]: "Registration Team",
  [AdminRole.OPERATIONS_TEAM]: "Operations Team",
  [AdminRole.SUPPORT_TEAM]: "Support Team",
  [AdminRole.FINANCE_TEAM]: "Finance Team",
};

/** Label for a stored role string, including legacy ones (superadmin → CEO). */
export function adminRoleLabel(role: string | null | undefined): string | undefined {
  const r = normalizeAdminRole(role);
  return r ? ADMIN_ROLE_LABELS[r] : (role ?? undefined);
}

export interface Admin {
  id: string;
  _id?: string;
  name: string;
  email: string;
  role: AdminRole;
  phoneNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginResult {
  tokens: AuthTokens;
  admin: Admin;
}

// ---- Dashboard analytics (GET /admin/analytics/dashboard) ----

export interface TrendData {
  direction: "up" | "down" | "neutral";
  value: string;
  period: string;
}

export interface UsersAnalytics {
  total: number;
  active: number;
  trend: TrendData;
}
export interface DriversAnalytics {
  total: number;
  active: number;
  pending: number;
  trend: TrendData;
}
export interface TripsAnalytics {
  total: number;
  completed: number;
  cancelled: number;
  inProgress?: number;
  trend: TrendData;
}
export interface RevenueAnalytics {
  total: number;
  platform: number;
  driver: number;
  trend: TrendData;
}
export interface SOSAlertsAnalytics {
  total: number;
  active: number;
}
export interface QueriesAnalytics {
  total: number;
  pending: number;
}
export interface LostItemsAnalytics {
  total: number;
  pending: number;
}
export interface RecentTrip {
  tripId: string;
  driverName: string;
  userName: string;
  fare: number;
  status: string;
}

export interface DashboardAnalytics {
  users: UsersAnalytics;
  drivers: DriversAnalytics;
  trips: TripsAnalytics;
  revenue: RevenueAnalytics;
  sosAlerts: SOSAlertsAnalytics;
  queries: QueriesAnalytics;
  lostItems: LostItemsAnalytics;
  recentTrips: RecentTrip[];
}

export type DashboardPeriod =
  | "today"
  | "last_7_days"
  | "last_30_days"
  | "this_month"
  | "last_3_months"
  | "last_year"
  | "all_time";
